import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Form, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import { BASE_URL } from "../../../../services/Api";
import { authHeader } from "../../../../services/auth-header";
import CommonSalesEnquiryFields from '../CommonSalesEnquiryFields';
import debounce from 'lodash/debounce';

function CreatePumpDomesticSalesEnquiry() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [customermaterialcode, setcustomermaterialcode] = useState('');

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
    const [rows, setRows] = useState([]);
    const [commonFormData, setCommonFormData] = useState({});

    const unitOptions = [
        { value: "", label: "Select UOM" },
        { value: "bot", label: "Bot" },
        { value: "ctn", label: "Ctn" },
        { value: "days", label: "Days" },
        { value: "ft", label: "Ft" },
        { value: "hrs", label: "Hrs" },
        { value: "kg", label: "Kg" },
        { value: "ltr", label: "Ltr" },
        { value: "mtr", label: "Mtr" },
        { value: "nos", label: "Nos" },
        { value: "pil", label: "Pil" },
        { value: "pkt", label: "Pkt" },
        { value: "set", label: "Set" },
        { value: "pcs", label: "Pcs" }
    ];

    const [enquirydate, setEnquiryDate] = useState('');
    const [customerenquirynumber, setcustomerenquirynumber] = useState('');
    const [enquiryno, setenquiryno] = useState('');
    const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
    const [isEnquiryValid, setIsEnquiryValid] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const checkEnquiryNo = async (number) => {
        if (!number) {
            setEnquiryValidationMsg('');
            setIsEnquiryValid(false);
            return;
        }

        // Basic validation first
        if (!/^[A-Za-z0-9-]+$/.test(number)) {
            setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
            setIsEnquiryValid(false);
            return;
        }

        try {
            const response = await axios.get(`${BASE_URL}enquiry/check/${number}`, {
                headers: authHeader(),
            });

            if (response.data === true) {
                setEnquiryValidationMsg('❌ This enquiry number already exists.');
                setIsEnquiryValid(false);
            } else if (response.data === false) {
                setEnquiryValidationMsg('✅ This enquiry number is available.');
                setIsEnquiryValid(true);
            } else {
                setEnquiryValidationMsg('Unexpected response from server.');
                setIsEnquiryValid(false);
            }
        } catch (error) {
            console.error("Error checking enquiry number:", error);
            setEnquiryValidationMsg('❌ Error validating enquiry number.');
            setIsEnquiryValid(false);
        }
    };


    useEffect(() => {
        const debouncedCheck = debounce(() => {
            checkEnquiryNo(enquiryno);
        }, 400);

        debouncedCheck();

        return () => debouncedCheck.cancel();
    }, [enquiryno]);

    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(encodeURIComponent(inputValue || " "));
    }, 300);

    const getProductData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );

            const products = response.data?.Index || response.data?.data || [];
            const mapped = products.map(product => ({
                value: product.id,
                label: `${product.description}  - ${product.serialnumber} - ${product.partno}`,
                description: product.description,
                itemcode: product.itemcode,
                hsnsacno: product.hsnsacno,
                partno: product.partno,
                serialnumber: product.serialnumber,
                modelnumber: product.modelnumber,
                tagnumber: product.tagnumber,
                moc: product.moc,
                uom: product.unitofmeasurement || unitOptions[0].value,
            }));

            setProductOptions(mapped);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    useEffect(() => {
        getProductData();
    }, [productSearch]);

    const handleProductChange = (selectedOption) => {
        if (!selectedOption) return;
        setSelectedProduct(selectedOption);

        const newRow = {
            description: selectedOption.description,
            value: selectedOption.value,
            label: selectedOption.label,
            quantity: '',
            partno: selectedOption.partno,
            hsnsacno: selectedOption.hsnsacno,
            itemcode: selectedOption.itemcode,
            serialnumber: selectedOption.serialnumber,
            modelnumber: selectedOption.modelnumber,
            tagnumber: selectedOption.tagnumber,
            moc: selectedOption.moc,
            uom: selectedOption.uom || unitOptions[0].value,
            productremarks: "",
        };

        setRows([...rows, newRow]);
    };

    const handleRowChange = (index, key, value) => {
        const updatedRows = [...rows];
        updatedRows[index][key] = value;
        setRows(updatedRows);
    };

    const handleQuantityChange = (index, value) => {
        const updatedRows = [...rows];
        updatedRows[index].quantity = value;
        setRows(updatedRows);
    };

    const removeRow = (index) => {
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        if (!isEnquiryValid) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid enquiry number before submitting.',
            });
            return;
        }

        // if (!validateForm()) return;



        const {
            customer,
            customerDetails,
            customerMultipleAddress,
        } = commonFormData;


        if (rows.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No Products Added',
                text: 'Please add at least one product to the enquiry.',
            });
            return;
        }

        const enquiryItems = rows.map(row => ({
            product: { id: row.value },
            quantity: row.quantity,
            itemcode: row.itemcode || '',
            hsnsacno: row.hsnsacno || '',
            partno: row.partno || '',
            uom: row.uom || '',
            serialnumber: row.serialnumber || '',
            modelnumber: row.modelnumber || '',
            tagnumber: row.tagnumber || '',
            productremarks: row.productremarks || '',
            moc: row.moc || '',
        }));

        const formData = {
            enquiryItems,
            enquirytype: "Pump Spare",
            enquirycategory: "Domestic",
            customer: { id: customer.value },
            contactno: customerDetails.contactno,
            contactperson: customerDetails.contactperson,
            email: customerDetails.email,
            customeraddress: { id: customerMultipleAddress?.value },
            enquirydate,
            customerenquirynumber,
            enquiryno,
            customermaterialcode,
        };

        try {
            await axios.post(`${BASE_URL}enquiry/save`, formData, {
                headers: authHeader()
            });
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'sales enquiry added successfully!'
            });
            navigate("/salesEnquiryIndex/1");
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to add enquiry.'
            });
        }
    };

    return (
        <div className="container">
            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Enquiry Number: *</Form.Label>
                            <Form.Control
                                type="text"
                                value={enquiryno}
                                onChange={e => {
                                    setenquiryno(e.target.value);
                                    setFormSubmitted(false);
                                }}
                                isInvalid={
                                    (formSubmitted && !isEnquiryValid) ||
                                    enquiryValidationMsg.includes('exists') ||
                                    enquiryValidationMsg.includes('Error') ||
                                    enquiryValidationMsg.includes('allowed')
                                }
                                required
                            />
                            {enquiryValidationMsg && (
                                <Form.Control.Feedback type={
                                    enquiryValidationMsg.includes('exists') ||
                                        enquiryValidationMsg.includes('Error') ||
                                        enquiryValidationMsg.includes('allowed') ?
                                        "invalid" : "valid"
                                }>
                                    {enquiryValidationMsg}
                                </Form.Control.Feedback>
                            )}
                            {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter a valid enquiry number
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Enquiry Date *</Form.Label>
                            <Form.Control
                                type="date"
                                value={enquirydate}
                                onChange={e => setEnquiryDate(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="enquiryno">
                            <Form.Label>Customer Material code :</Form.Label>
                            <Form.Control
                                type="text"
                                value={customermaterialcode}
                                onChange={(e) => setcustomermaterialcode(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="enquiryno">
                            <Form.Label>Customer Enquiry No. :</Form.Label>
                            <Form.Control
                                type="text"
                                value={customerenquirynumber}
                                onChange={(e) => setcustomerenquirynumber(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <CommonSalesEnquiryFields onDataChange={setCommonFormData} />

                <Form.Group controlId="productSelect">
                    <Form.Label>Select a Product *</Form.Label>
                    <Select
                        value={selectedProduct}
                        onInputChange={handleProductSearchChange}
                        onChange={handleProductChange}
                        options={productOptions}
                        placeholder="Search or select product"
                        isClearable
                    />
                </Form.Group>

                <div className="table-responsive mt-3">
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Product Description</th>
                                <th>Serial No.</th>
                                <th>Model No.</th>
                                <th>Tag No.</th>
                                <th>Part No.</th>
                                <th>MOC</th>
                                <th>HSN Code</th>
                                <th>Product Code</th>
                                <th>Quantity *</th>
                                <th>UOM *</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        {row.description}
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={row.productremarks}
                                            onChange={(e) => handleRowChange(index, 'productremarks', e.target.value)}
                                            placeholder="Remarks"
                                            className='remarkss'
                                        />
                                    </td>
                                    <td>{row.serialnumber}</td>
                                    <td>{row.modelnumber}</td>
                                    <td>{row.tagnumber}</td>
                                    <td>{row.partno}</td>
                                    <td>{row.moc}</td>
                                    <td>{row.hsnsacno}</td>
                                    <td>{row.itemcode}</td>
                                    <td>
                                        <Form.Control
                                            className='quantity'
                                            type="number"
                                            value={row.quantity}
                                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                                            required
                                            min="1"
                                        />
                                    </td>
                                    <td>
                                        <Select
                                            className='uom'
                                            value={unitOptions.find(option => option.value === row.uom)}
                                            onChange={(selected) => handleRowChange(index, 'uom', selected.value)}
                                            options={unitOptions}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: base => ({ ...base, zIndex: 9999 })
                                            }}
                                            required
                                        />
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            onClick={() => removeRow(index)}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                <button className="btn btn-primary mt-3" type="submit">SUBMIT</button>
            </Form>
        </div>
    );
}

export default CreatePumpDomesticSalesEnquiry;
